import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { toast } from '@/components/core/v2/toaster';
import { z as zod } from 'zod';

import type { Amenity } from '@/types/v2/amenity';
import type { CreateReservationRequest, ReservationDocument } from '@/types/v2/reservation';
import { createReservation, createReservationCheckout } from '@/api/reservations';
import { getAmenities } from '@/api/amenities';
import { useUser } from '@/hooks/v2/use-user';
import { getPresignedUploadUrl, uploadFileToPresignedUrl } from '@/utils/s3';

function depositAmount(amenity: Amenity | undefined): number {
  if (!amenity?.deposit_amount) return 0;
  return Number(amenity.deposit_amount);
}
function totalFeeAmount(amenity: Amenity | undefined): number {
  const v = amenity?.total_fee_amount ?? amenity?.fee_amount;
  if (v == null) return 0;
  return Number(v);
}

const schema = zod.object({
  amenity_id: zod.string().min(1, 'Amenity is required'),
  date: zod.date(),
  start_time: zod.date(),
  end_time: zod.date(),
}).refine((data) => data.end_time > data.start_time, {
  message: 'End time must be after start time',
  path: ['end_time'],
});

type Values = zod.infer<typeof schema>;

interface NewReservationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function NewReservationDialog({ open, onClose, onSuccess }: NewReservationDialogProps): React.JSX.Element {
  const { user, community } = useUser();
  const queryClient = useQueryClient();
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [documentFiles, setDocumentFiles] = React.useState<Record<string, File | null>>({});

  // Fetch amenities
  const { data: amenitiesData } = useQuery({
    queryKey: ['amenities', community?.id],
    queryFn: () => getAmenities(community?.id || ''),
    enabled: !!community?.id && open,
  });

  const amenities = amenitiesData?.amenities?.filter((a) => a.is_active) || [];

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      amenity_id: '',
      date: dayjs().toDate(),
      start_time: dayjs().hour(9).minute(0).toDate(), // Default to 9:00 AM
      end_time: dayjs().hour(10).minute(0).toDate(), // Default to 10:00 AM
    },
  });

  const selectedDate = watch('date');
  const selectedStartTime = watch('start_time');
  const selectedAmenityId = watch('amenity_id');
  const selectedAmenity = React.useMemo(
    () => amenities.find((a) => a.id === selectedAmenityId),
    [amenities, selectedAmenityId],
  );

  const depositFee = depositAmount(selectedAmenity);
  const totalFee = totalFeeAmount(selectedAmenity);
  const depositRequired = depositFee > 0;
  const requiredDocs = selectedAmenity?.required_documents ?? [];
  const needsDocuments = depositRequired && requiredDocs.length > 0;
  const hasAllDocuments = !needsDocuments || requiredDocs.every((type) => documentFiles[type]);

  React.useEffect(() => {
    setDocumentFiles({});
  }, [selectedAmenityId]);

  const mutation = useMutation({
    mutationFn: (data: CreateReservationRequest) => {
      if (depositRequired) {
        return createReservationCheckout(data);
      }
      return createReservation(data);
    },
    onSuccess: (response: any) => {
      if (response?.checkout_url) {
        setIsRedirecting(true);
        window.location.href = response.checkout_url;
        return;
      }
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['blocked-slots'] });
      toast.success('Reservation created successfully');
      reset();
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create reservation');
    },
  });

  const onSubmit = async (data: Values) => {
    const startDateTime = dayjs(data.date)
      .hour(dayjs(data.start_time).hour())
      .minute(dayjs(data.start_time).minute())
      .second(0);
    const endDateTime = dayjs(data.date)
      .hour(dayjs(data.end_time).hour())
      .minute(dayjs(data.end_time).minute())
      .second(0);

    let documents: ReservationDocument[] | undefined;
    if (needsDocuments && requiredDocs.length > 0) {
      documents = [];
      for (const docType of requiredDocs) {
        const file = documentFiles[docType];
        if (!file) continue;
        const { url, key } = await getPresignedUploadUrl(file);
        await uploadFileToPresignedUrl(url, file);
        documents.push({ type: docType, key, file_name: file.name });
      }
    }

    const requestData: CreateReservationRequest = {
      amenity_id: data.amenity_id,
      unit_id: user?.units?.[0]?.id,
      start_time: startDateTime.utc().format('YYYY-MM-DD HH:mm:ss'),
      end_time: endDateTime.utc().format('YYYY-MM-DD HH:mm:ss'),
      documents,
    };

    mutation.mutate(requestData);
  };

  const handleClose = () => {
    reset();
    setDocumentFiles({});
    onClose();
  };

  const setDocumentFile = (type: string, file: File | null) => {
    setDocumentFiles((prev) => ({ ...prev, [type]: file }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Reservation</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <Controller
                name="amenity_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={Boolean(errors.amenity_id)}>
                    <InputLabel>Amenity</InputLabel>
                    <Select {...field} label="Amenity">
                      {amenities.map((amenity) => (
                        <MenuItem key={amenity.id} value={amenity.id}>
                          {amenity.name}
                          {amenity.requires_approval ? ' (Approval required)' : ''}
                          {depositAmount(amenity) > 0
                            ? ` — Deposit $${depositAmount(amenity).toFixed(2)}`
                            : ''}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.amenity_id && (
                      <Box sx={{ mt: 0.5, color: 'error.main', fontSize: '0.75rem' }}>
                        {errors.amenity_id.message}
                      </Box>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid xs={12}>
              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Date"
                    value={dayjs(field.value)}
                    onChange={(newValue) => {
                      if (newValue) {
                        field.onChange(newValue.toDate());
                      }
                    }}
                    minDate={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(errors.date),
                        helperText: errors.date?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            {selectedAmenity && depositRequired && (
              <Grid xs={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="subtitle2" color="primary.main" sx={{ mb: 0.5 }}>
                    Required deposit
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    ${depositFee.toFixed(2)}
                  </Typography>
                  {totalFee > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Total fee for this amenity: ${totalFee.toFixed(2)}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    You&apos;ll pay this deposit to confirm. You&apos;ll be redirected to secure payment, then back here once complete.
                  </Typography>
                </Box>
              </Grid>
            )}

            {needsDocuments && (
              <Grid xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Required documents
                </Typography>
                {requiredDocs.map((docType) => (
                  <Box key={docType} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {docType}
                    </Typography>
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      sx={{ textTransform: 'none' }}
                    >
                      {documentFiles[docType] ? documentFiles[docType]!.name : 'Choose file'}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx,image/*"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          setDocumentFile(docType, f ?? null);
                        }}
                      />
                    </Button>
                  </Box>
                ))}
              </Grid>
            )}

            {selectedAmenity && (depositFee > 0 || totalFee > 0) && (
              <Grid xs={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Fees for {selectedAmenity.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Deposit
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {depositFee > 0 ? `$${depositFee.toFixed(2)}` : 'No deposit'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Total Fee
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {totalFee > 0 ? `$${totalFee.toFixed(2)}` : 'No fee'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}

            <Grid xs={12} md={6}>
              <Controller
                name="start_time"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    label="Start Time"
                    value={dayjs(field.value)}
                    onChange={(newValue) => {
                      if (newValue) {
                        field.onChange(newValue.toDate());
                      }
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(errors.start_time),
                        helperText: errors.start_time?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <Controller
                name="end_time"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    label="End Time"
                    value={dayjs(field.value)}
                    onChange={(newValue) => {
                      if (newValue) {
                        field.onChange(newValue.toDate());
                      }
                    }}
                    minTime={dayjs(selectedStartTime).add(15, 'minute')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(errors.end_time),
                        helperText: errors.end_time?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={mutation.isPending || isRedirecting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isPending || isRedirecting || (needsDocuments && !hasAllDocuments)}
          >
            {(mutation.isPending || isRedirecting)
              ? depositRequired
                ? 'Redirecting to payment...'
                : 'Creating...'
              : depositRequired
                ? `Make Deposit ($${depositFee.toFixed(2)})`
                : 'Create Reservation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}




